define("tmds/utils/tmdsUtils", [
    "jquery",
    "d3",
    "tmds/geometry/Extent",
    "tmds/renderers/UniqueValueRenderer",
    "tmds/symbols/CompositePointSymbol",
    "tmds/symbols/FieldSymbol",
    "tmds/symbols/SimpleLineSymbol",
    "tmds/symbols/SimplePointSymbol",
    "tmds/symbols/TextSymbol",
    "tmds/utils/symbolUtils"
], function(
    $, d3, Extent, UniqueValueRenderer, CompositePointSymbol, FieldSymbol, SimpleLineSymbol, SimplePointSymbol, TextSymbol, symUtils
) {
    "use strict";

    var Colors = {
        BLACK: [0, 0, 0, 1],
        BROWN: [183, 92, 12, 1],
        DARK_GREY: [130, 130, 130, 1],
        GREY: [191, 191, 191, 1]
    };

    var DefaultLineWidth = 4;

    /**
     * define how the tracks should be rendered based on the renderType field values
     * 	TMDS: render the tracks align with TMDS
     *  GRIT: render all tracks in black except to grey out "NotInSubdivision" tracks
     */
    var renderMode = {
        "TMDS": {
            "Normal": {
                color: Colors.BLACK,
                width: DefaultLineWidth
            },
            "Valid Shelf": {
                color: Colors.BLACK,
                width: DefaultLineWidth
            },
            "NotVisibleInGRIT": {
                color: Colors.BLACK,
                width: DefaultLineWidth
            },
            "DisableForRestrictions": {
                color: Colors.BLACK,
                width: DefaultLineWidth
            },
            "ZeroMP": {
                color: Colors.BLACK,
                width: DefaultLineWidth
            },
            "NotInSubdivision": {
                color: Colors.GREY,
                width: DefaultLineWidth
            },
            "Other": {
                color: Colors.GREY,
                width: DefaultLineWidth
            }
        },
        "GRIT": {
            "Normal": {
                color: Colors.BLACK,
                width: DefaultLineWidth
            },
            "Valid Shelf": {
                color: Colors.BLACK,
                width: DefaultLineWidth
            },
            "NotVisibleInGRIT": {
                color: Colors.GREY,
                width: 0
            },
            "DisableForRestrictions": {
                color: Colors.GREY,
                width: DefaultLineWidth
            },
            "ZeroMP": {
                color: Colors.GREY,
                width: DefaultLineWidth
            },
            "NotInSubdivision": {
                color: Colors.GREY,
                width: DefaultLineWidth
            },
            "Other": {
                color: Colors.BLACK,
                width: DefaultLineWidth
            }
        }
    };

    return {
        /**
         * return the track renderer
         * @param mode {String} "TMDS" | "GRIT"
         * @return {tmds/renderers/UniqueValueRenderer}
         * render the tracks based on the renderType attribute value
         */
        getTrackRenderer: function(mode) {
            var renderConfig = renderMode[mode || "TMDS"];
            var renderer = new UniqueValueRenderer({
                fieldName: "renderType"
            });

            for (var key in renderConfig) {
                renderer.addValue(key, new SimpleLineSymbol(renderConfig[key]));
            }

            return renderer;
        },

        /**
         * return the switch renderer
         * @return {tmds/renderers/UniqueValueRenderer}
         * render the switches based on the renderType attribute value
         * @note grey out the signals if renderType = NotInSubdivision
         */
        getSwitchRenderer: function() {
            // render a switch as a letter S inside a square box
            var funcCreateSwitchSym = function(options) {
                var symSwitch = new CompositePointSymbol();
                symSwitch.addSymbol(new SimplePointSymbol({
                    style: "square",
                    color: options.fillColor,
                    size: 15, // in pixels
                    outline: {
                        color: options.outlineColor || Colors.BLACK,
                        width: 1
                    }
                })).addSymbol(new TextSymbol({
                    text: "S",
                    color: options.fontColor || Colors.BLACK,
                    yOffset: 5,
                    font: {
                        family: "Tahoma",
                        size: 14
                    }
                }));

                return symSwitch;
            };

            // symbol when renderType in ("Normal", "Other")
            var symDefault = funcCreateSwitchSym({
                fillColor: [189, 195, 27]
            });

            // symbol when renderType = "NotInSubdivision"
            var symGrey = funcCreateSwitchSym({
                fillColor: Colors.GREY,
                outlineColor: Colors.DARK_GREY,
                fontColor: Colors.DARK_GREY
            });

            return new UniqueValueRenderer({
                fieldName: "renderType",
                defaultSymbol: symDefault,
                uniqueValueInfos: [{
                    value: "NotInSubdivision",
                    symbol: symGrey
                }]
            });
        },

        /**
         * return the signal renderer
         * @return {tmds/renderers/UniqueValueRenderer}
         * render the signal based on three field values: authorityType, type and renderType
         *      authorityType: "CTC" | "OMA"
         * 		type: "L" | "R"		// Left or Right
         * 		renderType: "Normal" | "Other" | "NotInSubdivision"
         * @note
         * grey out the signals if renderType = NotInSubdivision
         */
        getSignalRenderer: function() {
            // render a signal using a svg path differented by it type value
            var uvrSignal = new UniqueValueRenderer({
                fieldName: "trafficType",
                fieldName1: "type",
                fieldName2: "renderType",
                fieldDelimeter: ","
            });

            const SVG_PATH = {
                "CTC": "M0,0L2,0L2,7L5,7A5,5 0 1,1 5,9L2,9L2,16L0,16z",
                "OMA": "M0,0L2,0L2,7L5,7L5,0L19,8L5,16L5,9L2,9L2,16L0,16z"
            }

            const funcGetSymOptions = function(attrs) {
                const [authorityType, type, renderType] = attrs.split(",");
                let path_opt = { path: SVG_PATH[authorityType] };
                
                let angle_offset_opt = {};
                switch (type) {
                    case "R": angle_offset_opt = { angle: 0, yOffset: -8 }; break;
                    case "L": angle_offset_opt = { angle: 180, yOffset: 8 }; break;
                    case "U": angle_offset_opt = { angle: 270, xOffset: -14, yOffset: 16 }; break;
                    case "D": angle_offset_opt = { angle: 90, xOffset: 18, yOffset: 0 }; break;
                }

                let color_opt = {};
                switch (renderType) {
                    case "Normal":
                    case "Other":
                        color_opt = { color: Colors.DARK_GREY }; break;
                    case "NotInSubdivision":
                        color_opt = { color: Colors.GREY, outlineColor: Colors.DARK_GREY }; break;
                }

                return Object.assign({}, path_opt, angle_offset_opt, color_opt);
            }

            var funCreateSymbol = function(attrs) {
                let options = funcGetSymOptions(attrs);

                return new SimplePointSymbol({
                    style: "path",
                    path: options.path,
                    color: options.color,
                    angle: options.angle || 0,
                    xOffset: options.xOffset || 0,
                    yOffset: options.yOffset || 0, // the offset to make the signal visually connect to the tracks
                    size: 12,
                    outline: {
                        color: options.outlineColor || Colors.BLACK,
                        width: 1
                    }
                })
            };

            [
                "CTC,R,Normal",
                "CTC,R,Other",
                "CTC,R,NotInSubdivision",
                "CTC,L,Normal",
                "CTC,L,Other",
                "CTC,L,NotInSubdivision",
                "CTC,U,Normal",
                "CTC,U,Other",
                "CTC,U,NotInSubdivision",
                "CTC,D,Normal",
                "CTC,D,Other",
                "CTC,D,NotInSubdivision",
                "OMA,R,Normal",
                "OMA,R,Other",
                "OMA,R,NotInSubdivision",
                "OMA,L,Normal",
                "OMA,L,Other",
                "OMA,L,NotInSubdivision",
                "OMA,U,Normal",
                "OMA,U,Other",
                "OMA,U,NotInSubdivision",
                "OMA,D,Normal",
                "OMA,D,Other",
                "OMA,D,NotInSubdivision"
            ].forEach(symAttrs => {
                uvrSignal.addValue(symAttrs, funCreateSymbol(symAttrs));
            });

            return uvrSignal;
        },

        /**
         * return the interlocker renderer
         * @return {tmds/renderers/UniqueValueRenderer}
         * render the interlockers based on the renderType attribute value
         * @note grey out the interlockers if renderType = NotInSubdivision
         */
        getInterlockerRenderer: function() {
            var funcCreateInterLockerSymbol = function(options) {
                return new SimplePointSymbol({
                    style: "path",
                    path: "M1,8A7,7 0 0,1 15,8A7,7 0 0,1 1,8M2.344,2.344L13.656,13.656M13.656,2.344L2.344,13.656z",
                    color: options.fillColor,
                    size: 12,
                    xOffset: -8,
                    yOffset: -8,
                    outline: {
                        color: options.outlineColor || Colors.BLACK,
                        width: 1.2
                    }
                });
            };

            var symDefault = funcCreateInterLockerSymbol({
                    fillColor: [224, 224, 224, 1]
                }),
                symGrey = funcCreateInterLockerSymbol({
                    fillColor: Colors.GREY,
                    outlineColor: Colors.DARK_GREY
                });

            return new UniqueValueRenderer({
                fieldName: "renderType",
                defaultSymbol: symDefault,
                uniqueValueInfos: [{
                    value: "NotInSubdivision",
                    symbol: symGrey
                }]
            });
        },

        /**
         * return the control point renderer
         * @return {tmds/renderers/UniqueValueRenderer}
         * render the control points based on the renderType attribute value
         * @note grey out the control points if renderType = NotInSubdivision
         */
        getControlPointRenderer: function() {
            var funcCreateCPSym = function(options) {
                // each control point will display milepost, station name or control point name, and its guid
                var sym = new CompositePointSymbol();
                //				sym.addSymbol(new FieldSymbol({
                //					field: "milePost",
                //					mpColor: options.mpColor || Colors.BLACK,
                //					yOffset: -4,
                //					font: {
                //						family: "Tahoma",
                //						size: 11
                //					}
                //				}))
                sym.addSymbol(new FieldSymbol({
                    field: "name",
                    color: options.nameColor || Colors.BLACK,
                    yOffset: 3,
                    font: {
                        family: "Tahoma",
                        weight: "bold",
                        size: 12
                    }
                })).addSymbol(new FieldSymbol({
                    field: "uid",
                    color: options.idColor || Colors.BLACK,
                    yOffset: 15,
                    font: {
                        family: "Tahoma",
                        size: 10
                    }
                }));

                return sym;
            };

            // each control point will display milepost, station name or control point name, and its guid
            var symDefault = funcCreateCPSym({
                    idColor: Colors.BROWN
                }),
                symGrey = funcCreateCPSym({
                    nameColor: Colors.GREY,
                    idColor: Colors.GREY
                });

            return new UniqueValueRenderer({
                fieldName: "renderType",
                defaultSymbol: symDefault,
                uniqueValueInfos: [{
                    value: "NotInSubdivision",
                    symbol: symGrey
                }]
            });
        },

        /**
         * return the alpha text renderer
         * @return {tmds/renderers/UniqueValueRenderer}
         * render the alpha texts based on the renderType attribute value
         * @note grey out the alpha texts if renderType = NotInSubdivision
         */
        getAlphaTextRenderer: function() {
            var funcCreateSym = function(color) {
                return new FieldSymbol({
                    field: "name",
                    color: color || Colors.BLACK,
                    font: {
                        family: "Tahoma",
                        size: 11
                    }
                });
            };

            var symDefault = funcCreateSym(),
                symGrey = funcCreateSym(Colors.GREY);

            return new UniqueValueRenderer({
                fieldName: "renderType",
                defaultSymbol: symDefault,
                uniqueValueInfos: [{
                    value: "NotInSubdivision",
                    symbol: symGrey
                }]
            });
        }
    };
});